import React, { useState, useRef, useEffect } from "react";
import { MobileMainMenuProps, MenuItem } from "./types";
import { ListItems } from "./ListItems";
import {
    getCompactResponseDetails,
    getErrorMessage,
    logError,
} from "../../frontendLogger";

export const MobileMainMenu: React.VFC<MobileMainMenuProps> = ({
    menuItems,
    parentItem,
    startPageId,
    currentPageId,
    logotypeSettings,
    searchSettings,
    toolNavigationHtml,
    chevronIconSvg,
    searchIconSvg,
    hasLogotype,
    isintra,
    organizations,
    menuText = "Meny",
    closeMenuText = "Stäng",
    searchText = "Sök",
    closeSearchText = "Stäng",
    
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isSearchPanelOpen, setIsSearchPanelOpen] = useState<boolean>(false);
    const [activeMenuPage, setActiveMenuPage] = useState(parentItem);
    const [currentMenuItems, setCurrentMenuItems] = useState(menuItems);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const defaultText = "Välj förvaltning";


    // Hämta tidigare sparad text från localStorage eller använd standard
    const [selectedOption, setSelectedOption] = useState(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("selectedDropdownText") || defaultText;
        }
        return defaultText;
    });
    let isLoadingPages = false;
    const openCloseMenuButtonRef: React.RefObject<HTMLButtonElement> =
        useRef(null);

    useEffect(() => {
        const element = document.querySelector('.intra-header-content__personlink') as HTMLElement;
        if (element && element.innerText.trim() !== '') {
            setIsLoggedIn(true);
        } else {
            setIsLoggedIn(false);
        }

        const handleCloseMenu = (event: KeyboardEvent) => {
            if (event.key == "Escape") {
                if (isMenuOpen) onOpenMainMenuClick({});
                if (isSearchPanelOpen) onOpenSearchPanelClick({});
            }
        };

        document.addEventListener("keyup", handleCloseMenu);
        return () => {
            // Cleanup the event listener
            document.removeEventListener("keyup", handleCloseMenu);
        };


    }, [isMenuOpen, isSearchPanelOpen, isLoggedIn]);
    const handleSelect = (text: string, url: string) => {
        console.log("handleSelect körs med:", text);
        setSelectedOption(text)
        setIsOpen(!isOpen)
        if (typeof window !== "undefined") {
            localStorage.setItem("selectedDepartment", text);
        }

        if (url) {
            window.location.href = url; // Navigera till URL
        }
    };

    const onOpenMainMenuClick = (event) => {
        // Tar bort vertikal scroll på sidan när menyn är öppen
        document.documentElement.classList.toggle("overflow-hidden");
        setIsMenuOpen((current) => !current);
        setIsSearchPanelOpen(false);
    };

    const onOpenSearchPanelClick = (event) => {
        setIsSearchPanelOpen((current) => !current);
    };

    async function onMenuNavigationClick(
        event,
        action: string,
        clickedItem: MenuItem
    ) {
        event.preventDefault();

        const fetchForPageId =
            action === "next" ? clickedItem.pageId : clickedItem.parentPageId;
        const requestUrl =
            "/api/fetchmenuitems?" +
            new URLSearchParams({
                pageId: fetchForPageId.toString(),
                currentPageId: currentPageId.toString(),
                langName: clickedItem.langName,
            }).toString();

        if (isLoadingPages) {
            console.log("Hämtar menyalterantiv...");
            return;
        }

        isLoadingPages = true;

        await fetch(requestUrl)
            .then(async (response) => {
                if (!response.ok) {
                    logError({
                        category: "navigation",
                        eventName: "fetch-menu-items-failed",
                        componentOrWidget: "MobileMainMenu",
                        url: requestUrl,
                        data: {
                            status: response.status,
                            action,
                            pageId: fetchForPageId,
                            currentPageId,
                            langName: clickedItem.langName,
                            ...(await getCompactResponseDetails(response)),
                        },
                    });

                    throw new Error(`Request failed with status ${response.status}`);
                }

                return response.json();
            })
            .then((nextMenuItem: MobileMainMenuProps) => {
                setCurrentMenuItems(nextMenuItem.menuItems);
                clickedItem.isNonClickableInMobile = nextMenuItem.parentItem.isNonClickableInMobile
                const activeItem =
                    action === "next" ? clickedItem : nextMenuItem.parentItem;

                setActiveMenuPage(activeItem);

                isLoadingPages = false;
            })
            .catch((error: Error) => {
                isLoadingPages = false;
                if (!error.message.startsWith("Request failed with status ")) {
                    logError({
                        category: "navigation",
                        eventName: "fetch-menu-items-failed",
                        componentOrWidget: "MobileMainMenu",
                        url: requestUrl,
                        data: {
                            status: undefined,
                            action,
                            pageId: fetchForPageId,
                            currentPageId,
                            langName: clickedItem.langName,
                            error: getErrorMessage(error),
                        },
                    });
                }
            });
    }

    function Logotype() {
        const hasHeader: boolean =
            logotypeSettings.header !== null && logotypeSettings.header !== "";
        const hasCustomLogotype: boolean =
            (logotypeSettings.imageUrl !== null && logotypeSettings.imageUrl !== "") ||
            (logotypeSettings.imageInlineSvg !== null && logotypeSettings.imageInlineSvg !== "");

        return (
            <>
                {!isintra ? (
                    <div className="logotype">
                        <a
                            className="logotype__mobile dark ignore-readability-rule"
                            href={logotypeSettings.targetHref}
                            aria-label={hasHeader ? logotypeSettings.screenReaderLinkTextAriaLabel + logotypeSettings.header : logotypeSettings.screenReaderLinkTextGeneric}
                        >
                            {/* Visa enbart logotyp här om sitenamn inte är angivet. Annars visas logotypen ovanför navigeringen. */}
                            {(() => {
                                if (!hasHeader && hasCustomLogotype) {
                                    return (
                                        <>
                                            <div
                                                className={`logotype__default ${logotypeSettings.customOrganizationCssClass}`}
                                            >
                                                {logotypeSettings.imageInlineSvg ? (
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: logotypeSettings.imageInlineSvg,
                                                        }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={logotypeSettings.imageUrl}
                                                        alt={logotypeSettings.imageAltText}
                                                    />
                                                )}
                                            </div>
                                            <span className="sr-only">
                                                {logotypeSettings.screenReaderLinkTextGeneric}
                                            </span>
                                        </>
                                    );
                                } else if (hasHeader) {
                                    return (
                                        <div>
                                            <span>{logotypeSettings.header}</span>
                                        </div>
                                    );
                                } else if (!logotypeSettings.hideDefaultLogotype) {
                                    return (
                                        <>
                                            <div
                                                className={`logotype__default ${logotypeSettings.customOrganizationCssClass} ${logotypeSettings.isDefaultVgrLongLogotype ? "logotype__default--mobile-default-white-fill" : ""}`}
                                                dangerouslySetInnerHTML={{
                                                    __html: logotypeSettings.defaultLogotypeSvg,
                                                }}
                                            ></div>
                                            <span className="sr-only">
                                                {logotypeSettings.screenReaderLinkTextGeneric}
                                            </span>
                                        </>
                                    );
                                }

                            })()}
                        </a>
                    </div>
                ) : (
                    <div className="megamenu__home-wrapper">
                        <div className="megamenu__department">
                            <span className="megamenu__department__label megamenu__department__label--mobile"> Insidan</span>

                            <div className="megamenu__department__dropdown megamenu__department__dropdown--mobile ">
                                <button className="megamenu__department__dropdown-btn megamenu__department__dropdown-btn--mobile" id="dropdown-btn-mobile"
                                    onClick={() => setIsOpen(!isOpen)}>
                                    <span> {selectedOption}</span>
                                    <span dangerouslySetInnerHTML={{ __html: chevronIconSvg }}></span>
                                </button>

                                {isOpen && (
                                    <ul className="megamenu__department__dropdown-list show" id="dropdown-list-mobile">

                                        {organizations?.map((item) => (
                                            <li
                                                key={item.href}
                                                className="megamenu__department__dropdown-item"
                                                data-url={item.href}
                                                onClick={() => handleSelect(item.text, item.href)}
                                            >
                                                {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                )}


                            </div>

                        </div>
                    </div>
                )}
            </>
        );
    }


    function SearchPanel() {
        const hiddenSearchParameters = searchSettings.searchParameters.map(
            (tuple, index) => (
                <input
                    type="hidden"
                    key={index}
                    name={tuple.item1}
                    value={tuple.item2}
                />
            )
        );

        return (
            <div
                id="mobile-search-panel"
                className={`mobile-main-menu__search-panel mobile-container`}
            >
                <form
                    action={searchSettings.searchPageUrl}
                    method="get"
                    role="search"
                    autoComplete="off"
                    className="global-search intra-header-content__search mobile-search"
                >
                    <div className="global-search__search-box-button-container mobile-search__box-button-container">
                        {hiddenSearchParameters}

                        <input
                            type="hidden"
                            name="theme"
                            value={searchSettings.primaryColorName}
                        />
                        <input
                            type="search"
                            name="q"
                            placeholder={searchSettings.inputPlaceholderText}
                            id="search-input-mobile"
                            className="mobile-search__field"
                            aria-label={searchSettings.label}
                        />
                        <button type="submit" className="mobile-search__button">
                            <span className="sr-only">{searchText}</span>
                            <svg aria-hidden="true" focusable="false" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <use href={searchIconSvg}></use>
                            </svg>
                        </button>
                    </div>
                    <div id="mobile-autocomplete-result" className="hidden visually-hidden autocomplete mobile-autocomplete-result">

                        <div id="mobile-result-topdiv" className="autocomplete__content mobile-result-topdiv">

                        </div>
                        <div id="mobile-result-bottomdiv" className="autocomplete__content mobile-result-bottomdiv ">

                        </div>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <nav
            className={
                "mobile-main-menu container-fluid p-0 m-0 " +
                (isMenuOpen || isSearchPanelOpen ? "mobile-main-menu--open" : "")
            }
        >
            <div
                data-menu-open={isMenuOpen ? "true" : "false"}
                data-search-panel-open={isSearchPanelOpen ? "true" : "false"}
            ></div>

            <div className="mobile-main-menu__header">
                {isMenuOpen &&
                    activeMenuPage &&
                    startPageId !== activeMenuPage.pageId ? (
                    <>
                        <button
                            type="button"
                            className="mobile-main-menu__previous-navigation link-focus dark"
                            aria-label={`Visa undersidor till ${activeMenuPage.text}`}
                            dangerouslySetInnerHTML={{ __html: chevronIconSvg }}
                            onClick={(e) => {
                                activeMenuPage;
                                onMenuNavigationClick(e, "previous", activeMenuPage);
                            }}
                        ></button>
                        {!activeMenuPage.isNonClickableInMobile ? (<>
                            <a href={activeMenuPage.href} className="dark dark-text">
                                {activeMenuPage.text}
                            </a>
                        </>) : (<>
                            <span className="dark dark-text">
                                {activeMenuPage.text}
                            </span>
                        </>)}

                    </>
                ) : (
                    <Logotype />
                )}

                    <div className="mobile-main-menu__navigation-bar">

                        <div
                            className={`mobile-main-menu__search-button ${isMenuOpen ? "d-none" : ""
                                }`}
                        >
                            <button
                                className="link-focus dark"
                                aria-controls="mobile-search-panel"
                                aria-expanded={isSearchPanelOpen ? "true" : "false"}
                                onClick={onOpenSearchPanelClick}
                            >
                                <svg aria-hidden="true" focusable="false" width="30" height="30" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <use href={searchIconSvg}></use>
                                </svg>
                                <span>{isSearchPanelOpen ? closeSearchText : searchText}</span>
                            </button>
                        </div>
                        <div className="mobile-main-menu__menu-button">
                            <button
                                ref={openCloseMenuButtonRef}
                                className="link-focus dark"
                                aria-controls="mobile-menu-content"
                                aria-expanded={isMenuOpen ? "true" : "false"}
                                onClick={onOpenMainMenuClick}
                                data-testid="mobilemenubtn"
                            >
                                <span className="mobile-main-menu__burger">
                                    <span className="mobile-main-menu__burger-one"></span>
                                    <span className="mobile-main-menu__burger-two"></span>
                                    <span className="mobile-main-menu__burger-three"></span>
                                </span>
                                <span>{isMenuOpen ? closeMenuText : menuText}</span>
                            </button>
                        </div>

                    </div>
            </div>
            
            <SearchPanel />

            <div
                id="mobile-menu-content"
                tabIndex={
                    -1
                } /* This prevents tab focus for elements with "overflow:scroll" on Firefox */
                className={`mobile-main-menu__content ${isintra && isMenuOpen &&
                    activeMenuPage &&
                    startPageId !== activeMenuPage.pageId ? "more-space" : ""} ${hasLogotype ? "mobile-main-menu__content--with-logotype" : ""
                    } ${isLoggedIn ? "mobile-main-menu__content--with-inlogg" : ""} `}
            >
            {currentMenuItems?.length > 0 && (
                <div
                    className="mobile-main-menu__menu-items"
                    data-testid="mobilemenucontent"
                >
                    <ListItems
                        menuItems={currentMenuItems}
                        chevronIconSvg={chevronIconSvg}
                        onMenuNavigationClick={onMenuNavigationClick}
                        activeMenuPage={activeMenuPage}
                        startPageId={startPageId}
                    />
                </div>
            )}
                <div dangerouslySetInnerHTML={{ __html: toolNavigationHtml }} />
            </div>
        </nav>
    );
};
